import * as _ from 'lodash-es';
import React from 'react';
import ExternalBrandLogo from '../components/ExternalBrandLogo';
import { gql } from '../graphql/generated/gql';
import type { DocumentType } from '../graphql/generated/gql';
import SearchPlatformExternalPlatformTab from './SearchPlatformExternalPlatformTab';
import SearchPlatformTab from './SearchPlatformTab';
import SearchPlatformTabs from './SearchPlatformTabs';
import SearchResultTypeFilter from './SearchResultTypeFilter';
import * as styles from './SearchViewHeader.module.scss';

const PLATFORMS_FRAGMENT = gql(`
  fragment SearchViewHeader_platforms on ExternalSearchPlatform {
    personalAuthorized
    teamAuthorized
    platform
    ...SearchPlatformTabs_platforms
  }
`);

const VERTICAL_ICON_HEIGHT = 16;

type CountsByType = React.ComponentProps<
  typeof SearchResultTypeFilter
>['countsByType'];

interface SearchViewHeaderProps {
  activePlatformKey?: string;
  encouragedPlatformSet: Set<string>;
  platforms: DocumentType<typeof PLATFORMS_FRAGMENT>[];
  type?: string;
  setType: (type?: string) => void;
  setPlatform: (platform?: string) => void;
  externalCountsByType: CountsByType;
  countsByType: CountsByType;
  vertical: boolean;
  verticalExtra?: React.ReactNode;
  query: string;
  skip: boolean;
  loadPlatformData: (
    query: string,
    platform: string,
  ) => Promise<{ count: number | null }>;
}

const SearchViewHeader = ({
  activePlatformKey,
  encouragedPlatformSet,
  platforms,
  type,
  setType,
  setPlatform,
  vertical,
  externalCountsByType,
  countsByType,
  verticalExtra,
  query,
  skip,
  loadPlatformData,
}: SearchViewHeaderProps) => {
  const enabledPlatforms =
    platforms?.filter(
      (platform) =>
        platform.personalAuthorized ||
        platform.teamAuthorized ||
        encouragedPlatformSet.has(platform.platform),
    ) ?? [];

  const hasExternalTypedResults = _.sum(_.values(externalCountsByType)) > 0;

  const summedCounts = Object.entries(countsByType).reduce(
    (acc, [key, value]) => (key === 'comment' ? acc : acc + value),
    0,
  );

  const allResultCount = (() => {
    if (skip) return null;

    return countsByType.comment && countsByType.comment > 0
      ? `${summedCounts || countsByType.comment}+`
      : `${summedCounts}`;
  })();

  const hasSlabTypedResults = Object.keys(countsByType).length > 1;

  if (vertical) {
    return (
      <div className={styles.verticalHeaderSection}>
        <SearchPlatformTab
          icon={
            <ExternalBrandLogo brand="slab" height={VERTICAL_ICON_HEIGHT} />
          }
          active={!activePlatformKey}
          name="Slab"
          meta={allResultCount}
          className={styles.tab}
          activeClassName={styles.activeTab}
          onClick={() => setPlatform()}
        />
        {!activePlatformKey && hasSlabTypedResults && (
          <SearchResultTypeFilter
            type={type}
            setType={setType}
            vertical
            countsByType={countsByType}
          />
        )}
        {enabledPlatforms.map(({ platform }) => (
          <React.Fragment key={platform}>
            <SearchPlatformExternalPlatformTab
              active={activePlatformKey === platform}
              encourageInstallation={encouragedPlatformSet.has(platform)}
              key={platform}
              platformKey={platform}
              className={styles.tab}
              activeClassName={styles.activeTab}
              iconHeight={VERTICAL_ICON_HEIGHT}
              setPlatform={setPlatform}
              query={query}
              skip={skip}
              loadPlatformData={loadPlatformData}
            />
            {activePlatformKey === platform && hasExternalTypedResults && (
              <SearchResultTypeFilter
                type={type}
                setType={setType}
                vertical
                countsByType={externalCountsByType}
              />
            )}
          </React.Fragment>
        ))}
        {verticalExtra}
      </div>
    );
  }
  return (
    <>
      <div className={styles.headerSection}>
        <SearchPlatformTabs
          activePlatform={activePlatformKey}
          encouragedPlatformSet={encouragedPlatformSet}
          enabledPlatforms={enabledPlatforms}
          slabResultCount={allResultCount}
          setPlatform={setPlatform}
          query={query}
          loadPlatformData={loadPlatformData}
          skip={skip}
        />
      </div>
      {activePlatformKey
        ? hasExternalTypedResults && (
            <SearchResultTypeFilter
              type={type}
              setType={setType}
              countsByType={externalCountsByType}
            />
          )
        : hasSlabTypedResults && (
            <SearchResultTypeFilter
              type={type}
              countsByType={countsByType}
              setType={setType}
            />
          )}
    </>
  );
};

export default SearchViewHeader;
